// Round-trip helpers that let the MarkdownEditor edit a plain-text SMS
// body without losing newlines or merge-tag placeholders (DEV-8041).
//
// The phone-channel body is stored as plain text on the wire — SMS
// providers don't render HTML, and Viber/WhatsApp accept plain text +
// newlines too. The editor, however, works in HTML. We bridge:
//
//   plainText    →  plainTextToEditorHtml  →  editor HTML
//   editor HTML  →  htmlToSmsPlainText    →  plainText
//
// `convertPlainHtmlToEditorHtml` (in `LimeMarkdownEditor`) handles the
// chip layer: it converts `{tag}` text into chip spans on read and
// `stripNotificationTagsFromHtml` strips them back on save. By the time
// our `htmlToSmsPlainText` runs, the chip spans have already been
// replaced with `{tag}` text — but we still walk the DOM defensively in
// case a future caller passes raw editor HTML.

const BLOCK_TAGS = new Set([
  "p",
  "div",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
]);

const HTML_ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => HTML_ESCAPE_MAP[ch] ?? ch);
}

/**
 * A hyperlink in a message body travels as markdown (`[label](url)`)
 * because the wire format is plain text for every channel — the server
 * turns it back into an `<a>` when it renders a scheduled email
 * (DEV-10312). Only schemes a mail client will follow are recognized;
 * anything else stays literal text.
 */
const MARKDOWN_LINK =
  /\[([^\]\n]*)\]\(((?:https?:\/\/|mailto:|tel:)[^\s()]+)\)/g;

function escapePreservingLinks(line: string): string {
  let out = "";
  let cursor = 0;
  for (const match of line.matchAll(MARKDOWN_LINK)) {
    const label = match[1] ?? "";
    const url = match[2] ?? "";
    const start = match.index ?? 0;
    out += escapeHtml(line.slice(cursor, start));
    out += `<a href="${escapeHtml(url)}">${escapeHtml(label || url)}</a>`;
    cursor = start + match[0].length;
  }
  return out + escapeHtml(line.slice(cursor));
}

export type MarkdownSegment =
  | { kind: "text"; value: string }
  | { kind: "link"; label: string; href: string };

/**
 * Splits a line into its markdown links and the text between them, for
 * callers that render rather than serialize (the email preview). A link
 * whose href is a merge tag is matched too: tags resolve before the
 * server expands links, so `[Prestavi]({rescheduleLink})` does reach the
 * recipient as an anchor even though the toolbar cannot author one.
 */
export function splitMarkdownLinkSegments(line: string): MarkdownSegment[] {
  const segments: MarkdownSegment[] = [];
  let cursor = 0;
  for (const match of line.matchAll(PREVIEW_MARKDOWN_LINK)) {
    const label = match[1] ?? "";
    const href = match[2] ?? "";
    const start = match.index ?? 0;
    if (start > cursor) {
      segments.push({ kind: "text", value: line.slice(cursor, start) });
    }
    segments.push({ kind: "link", label: label || href, href });
    cursor = start + match[0].length;
  }
  if (cursor < line.length) {
    segments.push({ kind: "text", value: line.slice(cursor) });
  }
  return segments;
}

const PREVIEW_MARKDOWN_LINK =
  /\[([^\]\n]*)\]\(((?:https?:\/\/|mailto:|tel:)[^\s()]+|\{[a-zA-Z]+\})\)/g;

/**
 * Collapses markdown links into text an SMS can carry, for a draft moved
 * from the email channel to a phone channel. An autolinked URL (label
 * equal to the href) is already the whole message, so it stays bare.
 */
export function flattenMarkdownLinks(text: string): string {
  return text.replace(MARKDOWN_LINK, (_raw, label: string, url: string) =>
    !label || label === url ? url : `${label}: ${url}`,
  );
}

/**
 * Converts a plain-text SMS body into the HTML shape the markdown
 * editor expects. Each line becomes its own `<p>` so the editor
 * renders distinct paragraphs and Enter creates a real new paragraph
 * rather than collapsing into a single block.
 *
 * Empty lines become empty `<p></p>` — matching TipTap's own output
 * for an empty paragraph created by pressing Enter. Using `&nbsp;`
 * here instead would cause `LimeMarkdownEditor`'s sync effect to see
 * a permanent mismatch between editor HTML and the prop value, which
 * triggers `setContent` on every keystroke that touches a blank line
 * and resets the cursor.
 *
 * Merge-tag text (`{tagName}`) is preserved as-is — the editor's
 * `convertPlainHtmlToEditorHtml` runs after this and turns the text
 * into chip spans.
 */
export function plainTextToEditorHtml(
  text: string,
  { preserveLinks = false }: { preserveLinks?: boolean } = {},
): string {
  if (!text) return "";
  const renderLine = preserveLinks ? escapePreservingLinks : escapeHtml;
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  return lines.map((line) => `<p>${line ? renderLine(line) : ""}</p>`).join("");
}

/**
 * Serializes an anchor back to `[label](href)`. Falls back to plain
 * `label: href` when either half carries a character that would not
 * survive a markdown round-trip — a broken `[…](…)` would reach the
 * recipient as literal punctuation instead of a link.
 */
function serializeAnchor(el: Element): string {
  const parts: string[] = [];
  for (const child of Array.from(el.childNodes)) {
    walkForSmsText(child, parts, true);
  }
  const label = parts
    .join("")
    .replace(/\u00A0/g, " ")
    .trim();
  const href = el.getAttribute("href")?.trim() ?? "";

  if (!href) return label;
  if (!label || label === href) return href;
  if (/[[\]\n]/.test(label) || /[\s()]/.test(href)) return `${label}: ${href}`;
  return `[${label}](${href})`;
}

function walkForSmsText(
  node: Node,
  parts: string[],
  preserveLinks: boolean,
): void {
  if (node.nodeType === Node.TEXT_NODE) {
    // Browsers (and TipTap) often produce non-breaking spaces around
    // chips, after `&nbsp;`-padded blank paragraphs, and from
    // copy-paste. `U+00A0` looks like a space but flips the SMS
    // counter to UCS-2 and inflates per-segment cost. Fold to a
    // regular space at the source so saved bodies stay GSM-7.
    parts.push((node.textContent ?? "").replace(/\u00A0/g, " "));
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  // Defensive: if a chip span survived the editor's strip pass, emit
  // its `data-value` (the `{tag}` placeholder) so we don't drop tag
  // information when serializing to plain text.
  if (tag === "span" && el.hasAttribute("data-notification-tag")) {
    parts.push(el.getAttribute("data-value") ?? el.textContent ?? "");
    return;
  }

  if (tag === "br") {
    parts.push("\n");
    return;
  }

  if (preserveLinks && tag === "a") {
    parts.push(serializeAnchor(el));
    return;
  }

  for (const child of Array.from(el.childNodes)) {
    walkForSmsText(child, parts, preserveLinks);
  }

  // Append a newline after each block element so adjacent paragraphs
  // separate. Trailing newlines from the final block are trimmed by
  // the caller.
  if (BLOCK_TAGS.has(tag)) {
    parts.push("\n");
  }
}

/**
 * Serializes editor HTML into the plain-text shape the SMS / Viber /
 * WhatsApp pipeline expects. Block-element boundaries and `<br>` tags
 * become `\n`; chip spans collapse to their `{tag}` placeholder text;
 * everything else is dropped (the editor's toolbar is locked down to
 * exclude bold / italic / lists, so other elements appearing here
 * indicate a sanitization gap rather than intended formatting).
 *
 * Zero-width spaces inserted by `convertPlainHtmlToEditorHtml` for
 * cursor positioning are stripped — they would otherwise leak into the
 * outbound SMS as visible whitespace and waste a character.
 *
 * Only a single trailing `\n` is stripped — the one implicit in the
 * final block element closing. Stripping all trailing newlines made
 * Enter-at-end a no-op: the new empty `<p>` collapsed back to
 * nothing, body state didn't change, and the editor's sync effect
 * then wiped the just-typed empty paragraph.
 */
export function htmlToSmsPlainText(
  html: string,
  { preserveLinks = false }: { preserveLinks?: boolean } = {},
): string {
  if (!html) return "";
  // SSR or test environments without a DOM: bail out with the input
  // unchanged — the caller is responsible for not relying on this util
  // outside the browser.
  if (typeof document === "undefined") return html;

  const container = document.createElement("div");
  container.innerHTML = html;

  const parts: string[] = [];
  for (const child of Array.from(container.childNodes)) {
    walkForSmsText(child, parts, preserveLinks);
  }

  return parts
    .join("")
    .replace(/\u200B/g, "")
    .replace(/\n$/, "");
}
